/**
 * Groupmate × Horizon — productkaart-swatches
 *
 * Vervangt assets/mono.min.js (jQuery + hover-wissel). Geen dependencies.
 *
 * 1. Hover (alleen muis) over een groupmate-swatch: de kaart toont tijdelijk het andere product.
 * 2. Klik/tik op een swatch: de kaart blijft op dat product staan en de standaard quick-add van
 *    Horizon opent voor dat product. De swatch is daarmee de quick-add-knop; de gewone knop is
 *    bij groupmate-kaarten verborgen (CSS in layout/theme.liquid). Aan de quick-add zelf
 *    (assets/quick-add.js, de modal) verandert niets.
 *    Uitverkocht of geen quick-add mogelijk: de swatch opent de productpagina.
 *
 * Data komt server-side uit sections/gm-card-data.liquid (Section Rendering API), zodat
 * prijsnotatie, valuta en badges gelijk zijn aan wat Liquid in de kaart rendert.
 */

const SWATCH = '.gm_item--miniswatch';
const SWATCH_LIST = '.gm_miniswatch_list';
const DATA_SECTION = 'gm-card-data';
const SELECTED_CLASS = 'gm-selected';

const root = window.Shopify?.routes?.root || '/';

/** @type {Map<string, Promise<object>>} */
const cardDataCache = new Map();
/** @type {WeakMap<Element, {snapshot: object, originalHandle: string | null, selected: string | null, token: string | null}>} */
const cardState = new WeakMap();

/* ---------- helpers ---------- */

function productPath(handle) {
  return `${root}products/${encodeURIComponent(handle)}`;
}

/** @param {string | null | undefined} href */
function handleFromUrl(href) {
  if (!href) return null;
  try {
    const match = new URL(href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/** @param {Element} swatch */
function handleOfSwatch(swatch) {
  const el = swatch.closest('[data-product-handle]') || swatch.querySelector('[data-product-handle]');
  if (el instanceof HTMLElement && el.dataset.productHandle) return el.dataset.productHandle;
  const link = swatch.closest('a[href]') || swatch.querySelector('a[href]');
  return handleFromUrl(link?.getAttribute('href'));
}

function fetchCardData(handle) {
  if (cardDataCache.has(handle)) return cardDataCache.get(handle);
  const request = fetch(`${productPath(handle)}?section_id=${DATA_SECTION}`)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then(parseCardData)
    .catch((error) => {
      cardDataCache.delete(handle);
      throw error;
    });
  cardDataCache.set(handle, request);
  return request;
}

/** @param {string} html */
function parseCardData(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const json = doc.querySelector('script[data-gm-card]');
  if (!json) throw new Error('gm-card-data gaf geen data terug');
  const data = JSON.parse(json.textContent || '{}');
  data.priceHTML = doc.querySelector('template[data-gm-price]')?.innerHTML ?? null;
  data.badgesHTML = doc.querySelector('template[data-gm-badges]')?.innerHTML.trim() ?? null;
  return data;
}

/* ---------- card DOM ---------- */

/** @param {Element} card */
function parts(card) {
  const gallery = card.querySelector('.card-gallery');
  const slides = gallery
    ? [...gallery.querySelectorAll('slideshow-slide:not([hidden]), slideshow-slide[data-gm-hidden]')]
    : [];
  const quickAdd = card.querySelector('quick-add-component');
  return {
    links: card.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]'),
    galleryLink: card.querySelector('a[ref="cardGalleryLink"]'),
    titles: card.querySelectorAll(
      'a[ref="productTitleLink"] [role="heading"], a[ref="productTitleLink"] .title-text, .product-card__link .visually-hidden, .product-grid-view-zoom-out--details h3'
    ),
    prices: card.querySelectorAll('product-price [ref="priceContainer"]'),
    gallery,
    badges: gallery?.querySelector('.product-badges') || null,
    slides,
    images: slides.map((slide) => slide.querySelector('img')),
    quickAdd,
  };
}

const IMG_ATTRS = ['src', 'srcset', 'width', 'height', 'alt'];

/** @param {Element} card */
function takeSnapshot(card) {
  const p = parts(card);
  return {
    links: [...p.links].map((a) => a.getAttribute('href')),
    galleryLabel: p.galleryLink?.getAttribute('aria-label') ?? null,
    titles: [...p.titles].map((el) => el.textContent),
    prices: [...p.prices].map((el) => el.innerHTML),
    badges: p.badges?.outerHTML ?? null,
    galleryClass: p.gallery?.getAttribute('class') ?? null,
    images: p.images.map((img) =>
      img ? Object.fromEntries(IMG_ATTRS.map((name) => [name, img.getAttribute(name)])) : null
    ),
    featuredMediaUrl: card.getAttribute('data-featured-media-url'),
    quickAddTitle: p.quickAdd?.dataset.productTitle ?? null,
  };
}

/** @param {Element} el @param {string} name @param {string | null} value */
function setAttr(el, name, value) {
  if (value === null || value === undefined) el.removeAttribute(name);
  else el.setAttribute(name, value);
}

/** @param {Element} card @param {ReturnType<typeof takeSnapshot>} s */
function restore(card, s) {
  const p = parts(card);
  p.links.forEach((a, i) => setAttr(a, 'href', s.links[i] ?? null));
  if (p.galleryLink) setAttr(p.galleryLink, 'aria-label', s.galleryLabel);
  p.titles.forEach((el, i) => (el.textContent = s.titles[i] ?? el.textContent));
  p.prices.forEach((el, i) => {
    if (s.prices[i] !== undefined) el.innerHTML = s.prices[i];
  });
  if (p.badges && s.badges) p.badges.outerHTML = s.badges;
  if (p.gallery && s.galleryClass !== null) p.gallery.setAttribute('class', s.galleryClass);
  p.images.forEach((img, i) => {
    const attrs = s.images[i];
    if (img && attrs) IMG_ATTRS.forEach((name) => setAttr(img, name, attrs[name]));
  });
  p.slides.forEach((slide) => {
    if (!slide.hasAttribute('data-gm-hidden')) return;
    slide.removeAttribute('data-gm-hidden');
    slide.hidden = false;
  });
  setAttr(card, 'data-featured-media-url', s.featuredMediaUrl);
  if (p.quickAdd) setAttr(p.quickAdd, 'data-product-title', s.quickAddTitle);
}

/** @param {Element} card @param {any} d */
function apply(card, d) {
  const p = parts(card);
  p.links.forEach((a) => a.setAttribute('href', d.url));
  if (p.galleryLink) p.galleryLink.setAttribute('aria-label', d.title);
  p.titles.forEach((el) => (el.textContent = d.title));
  if (d.priceHTML !== null) p.prices.forEach((el) => (el.innerHTML = d.priceHTML));
  if (p.badges && d.badgesHTML) p.badges.outerHTML = d.badgesHTML;

  if (p.gallery) {
    [...p.gallery.classList]
      .filter((name) => name.startsWith('card-gallery--badge-'))
      .forEach((name) => p.gallery.classList.remove(name));
    if (d.hasBadges && d.badgePosition) p.gallery.classList.add(`card-gallery--badge-${d.badgePosition}`);
  }

  const images = d.images || [];
  p.images.forEach((img, i) => {
    const slide = p.slides[i];
    const source = images[i];
    // Minder foto's dan het originele product: overtollige slides verbergen.
    if (!source) {
      if (slide && i > 0 && images.length) {
        slide.setAttribute('data-gm-hidden', '');
        slide.hidden = true;
      }
      return;
    }
    if (slide?.hasAttribute('data-gm-hidden')) {
      slide.removeAttribute('data-gm-hidden');
      slide.hidden = false;
    }
    if (!img) return;
    img.setAttribute('srcset', source.srcset || source.src);
    img.setAttribute('src', source.src);
    img.setAttribute('width', String(source.width));
    img.setAttribute('height', String(source.height));
    img.setAttribute('alt', source.alt || '');
  });

  if (d.featuredMediaUrl && card.hasAttribute('data-featured-media-url')) {
    card.setAttribute('data-featured-media-url', d.featuredMediaUrl);
  }
  // Titel in de kop van de mobiele quick-add komt van dit attribuut.
  if (p.quickAdd) p.quickAdd.dataset.productTitle = d.title;
}

/** @param {Element} card */
function stateOf(card) {
  let state = cardState.get(card);
  if (!state) {
    const link = card.querySelector('a[ref="productCardLink"]');
    state = {
      snapshot: takeSnapshot(card),
      originalHandle: handleFromUrl(link?.getAttribute('href')),
      selected: null,
      token: null,
    };
    cardState.set(card, state);
  }
  return state;
}

/**
 * Toont een product in de kaart. Null of het eigen product = terug naar origineel.
 * @param {Element} card @param {string | null} handle
 */
async function show(card, handle) {
  const state = stateOf(card);
  if (!handle || handle === state.originalHandle) {
    state.token = null;
    restore(card, state.snapshot);
    return null;
  }
  state.token = handle;
  try {
    const data = await fetchCardData(handle);
    if (state.token !== handle || !card.isConnected) return null;
    apply(card, data);
    return data;
  } catch (error) {
    console.warn('[groupmate-card]', handle, error);
    if (state.token === handle) {
      state.token = null;
      restore(card, state.snapshot);
    }
    return null;
  }
}

/** @param {Element} card @param {Element | null} swatch */
function markSelected(card, swatch) {
  card.querySelectorAll(`${SWATCH}.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));
  swatch?.classList.add(SELECTED_CLASS);
}

/* ---------- events: collectiekaart ---------- */

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
  const swatch = event.target.closest(SWATCH);
  const card = swatch?.closest('product-card');
  if (!swatch || !card) return;

  const handle = handleOfSwatch(swatch);
  if (!handle) return;

  const state = stateOf(card);
  const target = handle === state.originalHandle ? null : handle;
  if (state.token === target) return;

  // Haal de andere swatches van deze kaart alvast op.
  swatch
    .closest(SWATCH_LIST)
    ?.querySelectorAll(SWATCH)
    .forEach((el) => {
      const h = handleOfSwatch(el);
      if (h && h !== state.originalHandle) fetchCardData(h).catch(() => {});
    });

  show(card, handle);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
  const list = event.target.closest(SWATCH_LIST);
  if (!list) return;
  if (event.relatedTarget instanceof Node && list.contains(event.relatedTarget)) return;
  const card = list.closest('product-card');
  if (!card) return;
  show(card, stateOf(card).selected);
});

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest(SWATCH);
    const card = swatch?.closest('product-card');
    if (!swatch || !card) return;

    const handle = handleOfSwatch(swatch);
    if (!handle) return;

    // Geen navigatie via de kaartlink of de app: de swatch is de quick-add.
    event.preventDefault();
    event.stopPropagation();

    const state = stateOf(card);
    state.selected = handle === state.originalHandle ? null : handle;
    markSelected(card, swatch);
    openQuickAdd(card, state.selected);
  },
  true
);

/**
 * Zet de kaart op het gekozen product en opent dan de standaard quick-add van Horizon.
 * QuickAddComponent haalt de product-URL uit de kaartlink, die show() net heeft bijgewerkt.
 * @param {Element} card @param {string | null} handle - null = origineel product
 */
async function openQuickAdd(card, handle) {
  const data = await show(card, handle);
  // Data van het andere product niet op te halen: dan naar de productpagina.
  if (handle && !data) {
    window.location.href = productPath(handle);
    return;
  }
  const quickAdd = card.querySelector('quick-add-component');
  const url = card.querySelector('a[ref="productCardLink"]')?.getAttribute('href');

  // Zelfde regel als Horizon: quick-add alleen als de instelling aan staat en het product leverbaar is.
  const allowed = data ? (isMobile() ? data.hasMobileQuickAdd : data.hasQuickAdd) : themeAllowsQuickAdd(card);

  if (!quickAdd || typeof quickAdd.handleClick !== 'function' || !allowed) {
    if (url) window.location.href = url;
    return;
  }
  quickAdd.handleClick(new Event('click', { cancelable: true }));
}

function isMobile() {
  return window.matchMedia('(max-width: 749px)').matches;
}

/** Leest de quick-add-zichtbaarheid die Liquid voor het originele product op de kaart zette. */
function themeAllowsQuickAdd(card) {
  const content = card.querySelector('.product-card__content');
  if (!(content instanceof HTMLElement)) return true;
  const name = isMobile() ? '--quick-add-mobile-display' : '--quick-add-display';
  return content.style.getPropertyValue(name).trim() !== 'none';
}
